import React, { useState, useEffect, useMemo } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import type { ColDef } from 'ag-grid-community'
import { useAppSelector } from '@/store/hooks'
import { TaskSummaryService, TaskSummaryTotalBusinessItem, TaskSummaryTotalDepartmentItem } from '@/api'
import { formatDateForAPI } from '@/components/ui/DateRangePicker/utils.ts'

interface DepartmentDetailPanelProps {
  isOpen: boolean
  onClose: () => void
  selectedDepartment: TaskSummaryTotalDepartmentItem | null
  startDate?: Date | null
  endDate?: Date | null
  periodViewType?: 'period' | 'all'
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 style={{
      fontFamily: designTokens.typography.fontFamily.sans[0],
      fontSize: '18px',
      fontWeight: 700,
      color: designTokens.colors.gray[6]
    }}>
      {title}
    </h3>
  </div>
)

const DepartmentDetailPanel: React.FC<DepartmentDetailPanelProps> = ({
  isOpen,
  onClose,
  selectedDepartment,
  startDate,
  endDate,
  periodViewType
}) => {
  const [businessData, setBusinessData] = useState<TaskSummaryTotalBusinessItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { selectedWorkspace } = useAppSelector(state => state.workspace)

  // Business summary column definitions
  const columnDefs: ColDef<TaskSummaryTotalBusinessItem>[] = useMemo(() => [
    {
      headerName: '사업명',
      field: 'business_name',
      width: 200,
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '14px',
        fontWeight: 400,
        color: designTokens.colors.gray[6]
      }
    },
    {
      headerName: '전체 수행업무',
      field: 'task_item.total.total_count',
      width: 120,
      cellRenderer: (params: any) => {
        const totalCount = params.data?.task_item?.total?.total_count
        const isNull = totalCount === null || totalCount === undefined
        return (
          <span style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            fontWeight: 600,
            color: designTokens.colors.gray[6]
          }}>
            {isNull ? '-' : totalCount.toLocaleString()}
          </span>
        )
      }
    },
    {
      headerName: '수행업무 진행율',
      field: 'task_item.total.count_percentage',
      width: 120,
      cellRenderer: (params: any) => {
        const percentage = params.data?.task_item?.total?.count_percentage
        const isNull = percentage === null || percentage === undefined
        return (
          <span style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            fontWeight: 600,
            color: designTokens.colors.primary.dark
          }}>
            {isNull ? '-' : `${percentage}%`}
          </span>
        )
      }
    },
    {
      headerName: '세부실행과제 진행율',
      field: 'task.total.count_percentage',
      width: 120,
      cellRenderer: (params: any) => {
        const percentage = params.data?.task?.total?.count_percentage
        const isNull = percentage === null || percentage === undefined
        return (
          <span style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            fontWeight: 600,
            color: designTokens.colors.primary.dark
          }}>
            {isNull ? '-' : `${percentage}%`}
          </span>
        )
      }
    },
    {
      headerName: '예산 집행율',
      field: 'task_budget.total.figure_percentage',
      width: 120,
      cellRenderer: (params: any) => {
        const percentage = params.data?.task_budget?.total?.figure_percentage
        const isNull = percentage === null || percentage === undefined
        return (
          <span style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            fontWeight: 600,
            color: designTokens.colors.secondary.dark
          }}>
            {isNull ? '-' : `${percentage}%`}
          </span>
        )
      }
    },
    {
      headerName: '성과 달성율',
      field: 'task_performance.total.count_percentage',
      width: 120,
      cellRenderer: (params: any) => {
        const percentage = params.data?.task_performance?.total?.count_percentage
        const isNull = percentage === null || percentage === undefined
        return (
          <span style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            fontWeight: 600,
            color: designTokens.colors.success.dark
          }}>
            {isNull ? '-' : `${percentage}%`}
          </span>
        )
      }
    }
  ], [])

  // Fetch business data when department changes
  useEffect(() => {
    const fetchBusinessData = async () => {
      // selectedDepartment가 null이면 리턴 (빈 문자열 ID는 "전체"를 의미하므로 허용)
      if (!selectedDepartment || !selectedWorkspace?.id) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const requestParams: any = {
          workspaceId: selectedWorkspace.id,
          page: 1,
          perPage: 1000
        }

        // department_id가 빈 문자열이 아닌 경우에만 포함 (빈 문자열 = "전체")
        if (selectedDepartment.department_id) {
          requestParams.departmentId = selectedDepartment.department_id
        }

        // Only include date parameters when periodViewType is 'period'
        if (periodViewType === 'period') {
          if (startDate) {
            requestParams.startDate = formatDateForAPI(startDate)
          }
          if (endDate) {
            requestParams.endDate = formatDateForAPI(endDate)
          }
        }

        const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryTotalByBusiness(requestParams)

        setBusinessData(response || [])
      } catch (err) {
        console.error('Failed to fetch business summary:', err)
        setError('사업별 현황 데이터를 불러오는데 실패했습니다.')
      } finally {
        setIsLoading(false)
      }
    }

    if (isOpen && selectedDepartment) {
      fetchBusinessData()
    }
  }, [isOpen, selectedDepartment?.department_id, selectedWorkspace?.id, startDate, endDate, periodViewType, selectedDepartment])

  if (!selectedDepartment) {
    return null
  }

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={`부서 상세: ${selectedDepartment.department_name}`}
      width={800}
    >
      <div className="p-6 space-y-6">
        {/* 기본 정보 섹션 */}
        <div>
          <SectionHeader title="기본 정보" />
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-5">
              <div className="w-20 flex-shrink-0">
                <span style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  fontWeight: 400,
                  color: designTokens.colors.gray[6]
                }}>
                  부서명
                </span>
              </div>
              <div className="flex-1 py-2">
                <span style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  fontWeight: 400,
                  color: designTokens.colors.gray[6]
                }}>
                  {selectedDepartment.department_name}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 사업별 현황 섹션 */}
        <div>
          <SectionHeader title="사업별 현황" />
          <div className="mt-4">
            {error ? (
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <span style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.error.dark
                }}>
                  {error}
                </span>
              </div>
            ) : (
              <DataGrid
                columnDefs={columnDefs}
                rowData={businessData}
                headerHeight={40}
                rowHeight={42}
                height={40 + (20 * 42) + 60 + 2} // header + (pageSize rows * rowHeight) + pagination height + border
                pagination={true}
                paginationPageSize={100}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                paginationAutoPageSize={false}
                loading={isLoading}
                rowSelection={undefined}
                // 범위 선택 및 복사 기능 활성화
                enableRangeSelection={true}
                enableCellCopy={true}
                suppressRowClickSelection={true}
                rangeCopyFormatter={(colId, value) => {
                  // 퍼센트 값 포맷팅
                  if (colId?.includes('percentage')) {
                    return value !== null && value !== undefined ? `${value}%` : '-'
                  }
                  if (value === null || value === undefined) return '-'
                  return value.toString()
                }}
              />
            )}
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default DepartmentDetailPanel